import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Flower, Menu } from "lucide-react";

interface HeaderProps {
  currentPage?: string;
  variant?: "home" | "gallery" | "default";
}

export default function Header({ currentPage, variant = "default" }: HeaderProps) {
  const isGallery = variant === "gallery";
  const isHome = variant === "home";

  const headerClasses = isHome 
    ? "container mx-auto px-4 py-4" 
    : "sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md shadow-sm";

  const containerClasses = isGallery || !isHome ? "container mx-auto px-4 py-4" : "";

  const logoClasses = "flex items-center gap-2";

  const logoIconClasses = isHome
    ? "h-6 w-6 text-purple-300"
    : "h-6 w-6 text-purple-700";

  const logoTextClasses = isHome
    ? "text-xl font-bold text-white"
    : "text-xl font-bold text-purple-800";

  const navClasses = "hidden md:flex items-center space-x-6";

  const linkClasses = isHome
    ? "text-purple-200 hover:text-white transition-colors duration-200"
    : "text-slate-700 hover:text-slate-900 transition-colors duration-200 font-medium";

  const activeLinkClasses = "text-purple-700 font-semibold";

  return (
    <header className={headerClasses}>
      <div className={containerClasses}>
        <nav className="flex items-center justify-between">
          <div className={logoClasses}>
            <Link to="/" className={logoClasses}>
              <Flower className={logoIconClasses} />
              <span className={logoTextClasses}>Lavender Flow</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="ml-8 hidden md:flex items-center space-x-6">
            {isHome ? (
              <>
                <Link to="#features" className={linkClasses}>
                  Features
                </Link>
                <Link to="#pricing" className={linkClasses}>
                  Pricing
                </Link>
                <Link to="#developers" className={linkClasses}>
                  Developers
                </Link>
                <Link to="#partners" className={linkClasses}>
                  Partners
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/integrations"
                  className={currentPage === "integrations" ? activeLinkClasses : linkClasses}
                >
                  Integrations
                </Link>
                <Link
                  to="/gallery/workflows"
                  className={currentPage === "workflows" ? activeLinkClasses : linkClasses}
                >
                  Workflows
                </Link>
                <Link
                  to="/gallery/flows"
                  className={currentPage === "flows" ? activeLinkClasses : linkClasses}
                >
                  Flow Store
                </Link>
                <Link to="/help" className={linkClasses}>
                  Help
                </Link>
                <Link to="/help" className={linkClasses}>
                  Community
                </Link>
                <Link to="/help" className={linkClasses}>
                  API
                </Link>
              </>
            )}
          </div>

          {/* Mobile Navigation (Hamburger Menu) */}
          <div className="md:hidden flex items-center">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle navigation</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetHeader>
                  <SheetTitle>Lavender Flow</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 p-4 h-full overflow-scroll">
                  {isHome ? (
                    <>
                      <Link to="#features" className={linkClasses}>
                        Features
                      </Link>
                      <Link to="#pricing" className={linkClasses}>
                        Pricing
                      </Link>
                      <Link to="#developers" className={linkClasses}>
                        Developers
                      </Link>
                      <Link to="#partners" className={linkClasses}>
                        Partners
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/integrations"
                        className={currentPage === "integrations" ? activeLinkClasses : linkClasses}
                      >
                        Integrations
                      </Link>
                      <Link
                        to="/gallery/workflows"
                        className={currentPage === "workflows" ? activeLinkClasses : linkClasses}
                      >
                        Workflows
                      </Link>
                      <Link
                        to="/gallery/flows"
                        className={currentPage === "flows" ? activeLinkClasses : linkClasses}
                      >
                        Flow Store
                      </Link>
                      <Link to="/help" className={linkClasses}>
                        Help
                      </Link>
                      <Link to="/help" className={linkClasses}>
                        Community
                      </Link>
                      <Link to="/help" className={linkClasses}>
                        API
                      </Link>
                    </>
                  )}
                  <div className="flex flex-col gap-2 mt-4">
                    <Link to="/login">
                      <Button variant={isHome ? "ghost" : "outline"} className="w-full">
                        {isHome ? "Log in" : "Sign In"}
                      </Button>
                    </Link>
                    {isHome && (
                      <Link to="/login">
                        <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white">Sign up</Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Login/Signup Buttons (Desktop) */}
          <div className="ml-auto hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button
                variant={isHome ? "ghost" : "outline"}
                size="sm"
                className={isHome
                  ? "text-purple-200 hover:text-white hover:bg-purple-800 transition-colors duration-200"
                  : "text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400 transition-colors duration-200"
                }
              >
                {isHome ? "Log in" : "Sign In"}
              </Button>
            </Link>
            {isHome && (
              <Link to="/login">
                <Button className="bg-purple-500 hover:bg-purple-600 text-white transition-colors duration-200">Sign up</Button>
              </Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}